import { useEffect, useState } from 'react';
import { getCategories } from './api.js';

const cache = new Map();
const empty = { tree: [], categories: [], meta: null, status: 'unavailable' };
const loading = { tree: [], categories: [], meta: null, status: 'loading' };
const keyFor = (tenantId) => tenantId == null || tenantId === '' ? '__default__' : String(tenantId);

export function getAslCategoryTreeStateForCacheKey(state, cacheKey) {
  return state?.cacheKey === cacheKey ? state.value : loading;
}

export function loadAslCategoryTree(tenantId) {
  const key = keyFor(tenantId);
  const cached = cache.get(key);
  if (cached) return cached instanceof Promise ? cached : Promise.resolve(cached);
  const pending = Promise.resolve().then(() => getCategories(tenantId)).then((response) => {
    const categories = Array.isArray(response?.categories) ? response.categories : [];
    const meta = response?.meta ?? null;
    const result = Array.isArray(response?.aslCategoryTree)
      ? { tree: response.aslCategoryTree, categories, meta, status: 'ready' }
      : { tree: [], categories, meta, status: 'unavailable' };
    if (cache.get(key) === pending) cache.set(key, result);
    return result;
  }).catch(() => {
    if (cache.get(key) === pending) cache.delete(key);
    return empty;
  });
  cache.set(key, pending);
  return pending;
}

export function clearAslCategoryTreeCache() { cache.clear(); }

export function useAslCategoryTree(tenantId) {
  const cacheKey = keyFor(tenantId);
  const [state, setState] = useState(() => {
    const cached = cache.get(cacheKey);
    const value = cached && !(cached instanceof Promise) ? cached : loading;
    return { cacheKey, value };
  });
  useEffect(() => {
    let current = true;
    const cached = cache.get(cacheKey);
    const value = cached && !(cached instanceof Promise) ? cached : loading;
    setState({ cacheKey, value });
    loadAslCategoryTree(tenantId).then((result) => {
      if (current) setState({ cacheKey, value: result });
    });
    return () => { current = false; };
  }, [cacheKey, tenantId]);
  return getAslCategoryTreeStateForCacheKey(state, cacheKey);
}
